import { ProviderGroup } from '../../../../lib/helpers/SmartSendBulkActionApplier';
import { VerifyShipResponse } from '~/lib/api/smartsend/types';
import useOnLoad from '~/routes/use-on-load';
import { useMemo, useState } from 'react';
import ProviderGroupSummaryTotalFallback from './ProviderGroupSummaryTotalFallback';
import ProviderGroupSummaryTotalVerified from './ProviderGroupSummaryTotalVerified';
import { SettingsCache } from '~/lib/consts/settings-cache';
import Loading from '~/components/loading/Loading';
import { GetQuoteServiceTagsFromItem } from '~/apps/smartsend/lib/helpers/SmartSendSummaryGenerator';
import { captureException } from '~/lib/metrics';

interface props {
  group: ProviderGroup;
  verifyShip: (group: ProviderGroup) => Promise<VerifyShipResponse>;
}

const ProviderGroupSummaryTotal: React.FC<props> = (props) => {
  const preBookingVerification = useMemo(() => {
    return (
      SettingsCache.settings.Features.includes('PreBookingVerification') ||
      props.group.Items.some((i) => GetQuoteServiceTagsFromItem(i).includes('QuoteApi'))
    );
  }, [props.group.Items]);

  const [loading, setLoading] = useState(preBookingVerification);
  const [breakdown, setBreakdown] = useState<Record<string, number> | null>(null);

  useOnLoad(() => {
    const onLoad = async () => {
      try {
        const res = await props.verifyShip(props.group);
        setBreakdown(res.Breakdown);
      } catch (error) {
        // Log error. No need to do anything else, we'll just show the fallback breakdown if this fails.
        captureException(error, 'Error verifying provider group breakdown', {});
      } finally {
        setLoading(false);
      }
    };
    if (preBookingVerification) {
      onLoad();
    }
  });

  if (loading) {
    return <Loading />;
  }

  const verifiedBreakdown = props.group.Discount?.Breakdown ?? breakdown;

  if (verifiedBreakdown) {
    return <ProviderGroupSummaryTotalVerified breakdown={verifiedBreakdown} currencyCode={props.group.CurrencyCode} />;
  }

  return <ProviderGroupSummaryTotalFallback group={props.group} />;
};

export default ProviderGroupSummaryTotal;
